import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { CircularProgressWithLabel } from "../../../components/CircularProgressWithLabel";
import { useCountDown } from "../../../hooks/useCountDown";
import { PaleteEnum } from "../../../interfaces/Enums";
import { ValidateCodeProps } from "../../../interfaces/ValidateCodeProps";
import { useAuthGenerateCode } from "../../../queries/Auth/generateCode";
import { useAuthValidateCode } from "../../../queries/Auth/validateCode";
import { useGlobalLoading } from "../../../store/GlobalLoading";
import { useSignUp } from "../../../store/SignUp";
import {
  ICodeForEmail,
  codeForEmailSchema,
  defaultValuesValidateCode,
} from "./validation";

export default function ValidateCode() {
  const { generateCode } = useAuthGenerateCode();
  const { validateCode } = useAuthValidateCode();
  const { signUpState, clearSignUpState } = useSignUp();
  const { openLoading } = useGlobalLoading();
  const { count, resetCountdown, stopCountdown } = useCountDown();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ICodeForEmail>({
    defaultValues: defaultValuesValidateCode,
    resolver: yupResolver(codeForEmailSchema.required()),
  });

  const handleValidateCode = async (data: ICodeForEmail) => {
    const request: ValidateCodeProps = {
      code: signUpState.code,
      inputCode: data.code,
    };

    openLoading();
    const result = await validateCode(request);
    if (result) {
      stopCountdown();
      reset(defaultValuesValidateCode);
    }
  };

  return (
    <Grid container flexDirection={"column"} p={4}>
      <Typography variant="h5" textAlign={"center"} fontWeight={"bold"} mb={4}>
        Confirme seu email
      </Typography>
      <Typography variant="body1" mb={2}>
        Um código de segurança foi enviado para
        <strong> {signUpState.email}</strong>
      </Typography>
      <Grid container rowSpacing={1}>
        <Grid item container columnGap={1.5} alignItems={"center"} mb={0.8}>
          {count !== 0 && (
            <Tooltip
              title={"Aguarde o tempo acabar para reenviar o código"}
              placement={"top"}
              arrow
            >
              <Box>
                <CircularProgressWithLabel value={count} />
              </Box>
            </Tooltip>
          )}
          <Typography
            variant="caption"
            fontStyle={"italic"}
            sx={{
              textDecoration: "underline",
              cursor: `${count === 0 && "pointer"}`,
            }}
            onClick={() => {
              if (count === 0) {
                generateCode(signUpState.email);
                resetCountdown();
              }
            }}
          >
            Reenviar o código
          </Typography>
        </Grid>
        <Grid item container>
          <TextField
            variant="outlined"
            label="Código"
            inputProps={{ inputMode: "numeric" }}
            fullWidth
            error={!!errors["code"]}
            {...register("code")}
          />
          <Typography variant="caption" mt={0.5} color={"red"}>
            {errors?.code?.message}
          </Typography>
        </Grid>
        <Grid item container flexDirection={"column"} rowSpacing={1}>
          <Grid item>
            <Button
              variant={"contained"}
              sx={{
                bgcolor: PaleteEnum.bluePrimary,
                "&:hover": {
                  bgcolor: PaleteEnum.bluePrimary,
                },
              }}
              fullWidth
              onClick={handleSubmit(handleValidateCode)}
            >
              Continuar
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={clearSignUpState}
              sx={{ color: PaleteEnum.bluePrimary }}
              fullWidth
            >
              Voltar
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
